import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';

import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { NgbDropdown }        from '@ng-bootstrap/ng-bootstrap';
import { TransitionOptions }  from '@uirouter/core';
import { TranslateService }   from '@ngx-translate/core';

import { ApiService }                from '../api/api.service';
import { CompanyMessagesModel }      from '../companies/models/company.messages';
import { CompanyService }            from '../companies/company.service';
import { GlobalSearchCategoryModel } from './typings/global-search.result';
import { JsonService }               from '../core/services/json.service';
import { LoanCarService }            from '../loan-car-schedule/loan-car.service';
import { LoanCarVehicleModel }       from '../loan-car-schedule/typings/loan-car.vehicle';
import { MessagesService }           from '../messages/messages.service';
import { MessengerEmailParamsModel } from '../messages/messenger/typings/messenger.email-params';
import { MessengerService }          from '../messages/messenger/messenger.service';
import { MessengerSmsParamsModel }   from '../messages/messenger/typings/messenger.sms-params';
import { ProductService }            from '../products/product.service';
import { SessionService }            from '../sessions/session.service';
import { SidebarService }            from '../sidebar/sidebar.service';
import { TypeaheadControlComponent } from '../shared/controls/typeahead-control/typeahead-control.component';

@Component({
  selector   : 'wor-global-search',
  styles: [ require('./global-search.component.scss') ],
  template: require('./global-search.component.html')
})
export class GlobalSearchComponent implements OnInit {
  delay     = 1000;
  query     = '';
  uiOptions : TransitionOptions = {
    inherit: false,
    reload : true
  };

  companyMessages: CompanyMessagesModel;
  results        : Array<GlobalSearchCategoryModel>;
  serverTimeout  : number;
  stream         : EventSource;

  @BlockUI('globalSearchBlock') block       : NgBlockUI;

  @ViewChild('searchControl') searchControl : TypeaheadControlComponent;

  @ViewChildren(NgbDropdown) actionDropdowns: QueryList<NgbDropdown>;

  constructor (
    private apiService      : ApiService,
    public companyService   : CompanyService,
    private elementRef      : ElementRef<any>,
    private jsonService     : JsonService,
    private loanCarService  : LoanCarService,
    private messagesService : MessagesService,
    private messengerService: MessengerService,
    private productService  : ProductService,
    public  sessionService  : SessionService,
    private sidebarService  : SidebarService,
    private translateService: TranslateService
  ) {}

  ngOnInit () : void {
    this.companyService.getDefaultMessages()
    .then(companyMessages => {
      this.companyMessages = companyMessages;
    });
  }

  @HostListener('document:mousedown', ['$event'])
  _handleOutsideClick ( event : Event ) {
    if ( this.query && !this.elementRef.nativeElement.contains(event.target) ) {
      this.clearSearch(event);
    }
  }

  clearSearch ( event ?: Event ) : void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.results     = [];
    this.query       = '';

    this.searchControl.elementRef.nativeElement.blur();
    this.sidebarService.reset();
  }

  doesCustomerHaveContact ( item : any ) : boolean {
    return item.contact_first
      || item.contact_last
      || item.contact_phone;
  }

  doesItemHaveStatus ( header : any ) : boolean {
    return header.table_name === 'inspections'
      || header.table_name === 'invoices'
      || header.table_name === 'vendor_invoices';
  }

  doesVendorHaveContact ( item : any ) : boolean {
    return item.contact1_first
      || item.contact1_last
      || item.contact1_phone;
  }

  formatCategory ( data : Array<any> ) : GlobalSearchCategoryModel {
    const header = data.filter(( item : any ) => item.category === 'headers')[0];
    const items  = data.filter(( item : any ) => item.category === header.table_name);

    if ( this.doesItemHaveStatus(header) ) {
      items.map(item => {
        item.status_description = item.status_description
          ?.replace(/\s+/g, '-')
          .toLowerCase();

        return item;
      });
    }

    else if ( header.table_name === 'products' ) {
      items.map(product => this.productService.formatSearchableTags(product));
    }

    return {
      category: header.table_name,
      items,
      title   : header.title
    };
  }

  formatter = ( item : any ) => {
    return this.query;
  }

  getProductQuantity ( product : any ) : string {
    return this.companyService.hasReservedStock()
      ? product.qty_available
      : product.quantity_on_hand;
  }

  getTotalItems ( results : Array<GlobalSearchCategoryModel> ) : number {
    const items = [];

    results.forEach(result => {
      items.push(result.items);
    });

    return items.length;
  }

  openSearch () : void {
    this.searchControl.elementRef.nativeElement.focus();
  }

  /**
   * This needs to be an inline function to that "this" is
   * maintained when called from typeahead control.
   */
  search = ( typed : string ) : ng.IPromise<Array<any>> => {
    return new Promise(( resolve, reject ) => {
      this.results = [];

      if ( !typed || typed.length < 2 ) {
        return resolve([]);
      }

      if ( this.serverTimeout ) {
        clearTimeout(this.serverTimeout);

        this.results = [];

        this.block.stop();

        if ( this.stream ) {
          this.stream.close();
        }
      }

      typed = this.apiService.encode(typed, 'get');

      this.serverTimeout = window.setTimeout(() => {
        const url = `${ this.apiService.getEndpoint() }/searches/${ typed }/all`;

        this.block.start();

        this.stream = new EventSource(url, { withCredentials: true });

        this.stream.addEventListener('message', ( event : MessageEvent ) => {
          this.serverTimeout = null;

          const category = this.formatCategory(this.jsonService.parse(event.data));

          if ( !category.items.length ) {
            this.block.stop();
            this.stream.close();

            return resolve(this.results);
          }

          this.results.push(category);

          if ( this.results && this.getTotalItems(this.results) >= 15 ) {
            this.block.stop();
            this.stream.close();

            return resolve(this.results);
          }
        });

        this.stream.addEventListener('error', () => {
          this.sessionService.validate()
          .catch(() => {
            this.sessionService.setKey('error_msg', this.translateService.instant('JS_SPACE.MESSAGES.INVALID_SESSION'));

            window.location.assign('/login.html');
          });

          this.block.stop();
          this.stream.close();

          return resolve(this.results);
        });
      }, this.delay);
    });
  }

  sendEmail ( emailParams : MessengerEmailParamsModel, event : Event ) : void {
    this.clearSearch(event);

    this.messengerService
    .email({
      body         : emailParams.body,
      email_address: emailParams.email,
      name         : emailParams.name
    })
    .then(() => {
      this.messagesService.show(
        this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'),
        'success'
      );
    });
  }

  sendSms ( smsParams : MessengerSmsParamsModel, event : Event ) : void {
    this.clearSearch(event);

    this.messengerService
    .sms({
      customer_id: null,
      name       : smsParams.name,
      send_to    : smsParams.sendTo,
    })
    .then(() => {
      this.messagesService.show(
        this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'),
        'success'
      );
    });
  }

  toggleActionMenu ( dropdown : NgbDropdown, event : PointerEvent ) : void {
    const thisDropdownElement = ( dropdown as any )._elementRef.nativeElement;

    this.actionDropdowns.toArray().forEach(actionDropdown => {
      const dropdownElement = ( actionDropdown as any )._elementRef.nativeElement;

      if (!thisDropdownElement.isSameNode(dropdownElement)) {
        actionDropdown.close();
      }
    });

    event.stopPropagation();
  }

  viewLoanVehicle ( loanVehicle : LoanCarVehicleModel ) : void {
    this.loanCarService.manageLoanVehicle(loanVehicle.id);
  }
}
